import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { JobCategoryDirectory } from 'pages/Bridges/ManagerValidation/components/JobCategoryDirectory';
import { theme } from '../../../../../components/layouts/theme';
import AppWrappedTextbox from '../AppWrappedTextbox';
import FormSection from '../FormSection';
import AlertMonitor from './components/AlertMonitor';
import DefaultDepartmentField from './components/DefaultDepartmentField';
import HybridContainer from './components/HybridContainer';
import NPINumberField, {
	ValidateYupNPI,
} from '../1617BridgesLink/components/NPINumberField';
import { ResearchSupplement } from 'pages/Bridges/ManagerValidation/components/Supplements/ResearchSupplement';
import HelpBar, { Main } from './components/HelpBar';
import { EPIC_APP_ID, EPIC_COMPONENT_ID, EPIC_FIELD_KEYS } from './_const';
import { extractValues } from './_util';
import JobCategoryTable from './components/JobCategoryTable';
import { getSupplementsByTrackingNumber } from 'pages/Bridges/ManagerValidation/_api';
import { OBJ_SUPPLEMENTS } from 'pages/Bridges/ManagerValidation/_constants';
import { BillingSupplement } from 'pages/Bridges/ManagerValidation/components/Supplements/BillingSupplement';
import { RadiologySupplement } from 'pages/Bridges/ManagerValidation/components/Supplements/RadiologySupplement';
import { AnesthesiologySupplement } from 'pages/Bridges/ManagerValidation/components/Supplements/AnesthesiologySupplement';
import {
	DepartmentSupplement,
	INITIAL_VALUES as DEPARTMENT_INITIAL_VALUES,
	FORM_FIELD_KEYS as DEPARTMENT_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/DepartmentSupplement';
import {
	SpecialtySupplement,
	FORM_FIELD_KEYS as SPECIALTIES_FORM_FIELD_KEYS,
	INITIAL_VALUES as SPECIALTIES_INITIAL_VALUES,
} from 'pages/Bridges/ManagerValidation/components/Supplements/SpecialtySupplement';

const Bridges = props => {
	const isModificationRequest = useSelector(state => {
		const formIndex = Object.keys(state.apps.appsData).find(
			formIndex => state.apps.appsData[formIndex].id === props.appId,
		);
		return formIndex
			? state?.apps?.appsData[formIndex]?.Action === 'ModifyAccess'
			: false;
	});

	const hasSubmissionBeenAttempted = useSelector(
		state => state.apps.submissionAttempted,
	);
	const trackingNumber = useSelector(state => state.apps.trackingNumber);

	const previousSessionValue = useSelector(state => {
		const formIndex = Object.keys(state.apps.allFormsInitial).find(
			formIndex =>
				state?.apps?.allFormsInitial[formIndex]?.id === props.appId,
		);
		const memberKey = formIndex
			? Object.keys(
					state?.apps?.allFormsInitial[formIndex]?.model?.members,
				).find(
					k =>
						state.apps.allFormsInitial[formIndex].model.members[k]
							.id === EPIC_COMPONENT_ID,
				)
			: null;
		const previousSessionData = memberKey
			? state.apps.allFormsInitial[formIndex].model.members[memberKey]
					.value
			: null;
		if (previousSessionData) {
			if (
				previousSessionData &&
				typeof previousSessionData === 'string'
			) {
				try {
					return JSON.parse(previousSessionData);
				} catch {
					return null;
				}
			} else {
				return null;
			}
		} else {
			return null;
		}
	});

	const [sJobCategoryHelpSelection, setJobCategoryHelpSelection] =
		useState(null);
	/**
	 * Array of supplement key,value pairs indicating the type of supplement per selected job category.
	 * @type [{Key: number, Value: string}[], Function]
	 */
	const [sSupplementKVPs, setSupplementKVPs] = useState(
		previousSessionValue?.[EPIC_FIELD_KEYS.HAS_SUPPLEMENTS] ?? [],
	);
	const [sOpen, setOpen] = useState(false);

	const validationSchema = useMemo(() => {
		let shape = extractValues('validationSchema', null);
		// separately defining schema for NPI due to its reliance on tracking number which does not exist in formik context
		shape[EPIC_FIELD_KEYS.NPI] = Yup.string()
			.maxCounter(10)
			.test('isNpiValidWhenRequired', async function (value) {
				if (!this.parent?.[EPIC_FIELD_KEYS.NEED_NPI]) {
					return true;
				}

				return await ValidateYupNPI(value, this, trackingNumber);
			});
		return Yup.object().shape(shape);
	}, [trackingNumber]);
	const jobCategoryDirectoryKey = Date.now();

	/**
	 * Resolves NPI requirement using only the changed job category event.
	 *
	 * @param {import('formik').FormikProps<object>} formik Formik context for field updates.
	 * @param {{
	 *  jobCategory: {jobCategoryId: number|null},
	 *  previousJobCategory: {jobCategoryId: number|null}
	 * }} changePayload Change metadata emitted by JobCategoryTable.
	 * @returns {Promise<void>}
	 */
	const onJobCategoriesChange = async (formik, changePayload) => {
		const { jobCategory, previousJobCategory } = changePayload ?? {};
		const previousJobCategoryId = Number(
			previousJobCategory?.jobCategoryId,
		);
		const nextJobCategoryId = Number(jobCategory?.jobCategoryId);
		let supplementKVPs = [...sSupplementKVPs];

		if (Number.isFinite(previousJobCategoryId)) {
			supplementKVPs = supplementKVPs.filter(
				s => s.Key !== previousJobCategoryId,
			);
		}

		if (Number.isFinite(nextJobCategoryId)) {
			try {
				const supplements = await getSupplementsByTrackingNumber(
					nextJobCategoryId,
					trackingNumber,
				);
				if (Array.isArray(supplements)) {
					supplements.forEach(supplementType => {
						if (
							!supplementKVPs.some(
								kvp =>
									kvp.Key === nextJobCategoryId &&
									kvp.Value === supplementType,
							)
						) {
							supplementKVPs.push({
								Key: nextJobCategoryId,
								Value: supplementType,
							});
						}
					});
				} else {
					supplementKVPs = supplementKVPs.filter(
						kvp => kvp.Key !== nextJobCategoryId,
					);
				}
			} catch {
				return;
			}
		}

		const needsDepartment = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.DEPARTMENT,
		);
		const needsNPI = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.NPI,
		);
		const needsResearch = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.RESEARCH,
		);
		const needsBilling = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.BILLING,
		);
		const needsRadiology = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.RADIOLOGY,
		);
		const needsAnesthesiology = supplementKVPs.some(
			kvp => kvp.Value === OBJ_SUPPLEMENTS.ANESTHESIOLOGY,
		);
		const needsSpecialties = supplementKVPs.some(
				kvp => kvp.Value === OBJ_SUPPLEMENTS.SPECIALTIES,
			);

		setSupplementKVPs(supplementKVPs);
		formik.setFieldValue(
			EPIC_FIELD_KEYS.HAS_SUPPLEMENTS,
			supplementKVPs,
			false,
		);
		formik.setFieldValue(
			EPIC_FIELD_KEYS.NEED_DEPARTMENT,
			needsDepartment,
			false,
		);
		if (!needsDepartment) {
			formik.setFieldValue(
				EPIC_FIELD_KEYS.DEPARTMENT,
				DEPARTMENT_INITIAL_VALUES[
					DEPARTMENT_FORM_FIELD_KEYS.DEPARTMENTS
				],
				false,
			);
		}

		formik.setFieldValue(EPIC_FIELD_KEYS.NEED_NPI, needsNPI, false);
		if (!needsNPI) {
			formik.setFieldValue(EPIC_FIELD_KEYS.NPI, '', false);
			formik.setFieldValue(
				EPIC_FIELD_KEYS.NPI_VERIFICATION,
				false,
				false,
			);
		}

		formik.setFieldValue(EPIC_FIELD_KEYS.NEED_BILLING, needsBilling, false);
		if (!needsBilling) {
			formik.setFieldValue(EPIC_FIELD_KEYS.BILLING, '', false);
			formik.setFieldValue(EPIC_FIELD_KEYS.BILLING_NEEDS, '', false);
		}

		formik.setFieldValue(
			EPIC_FIELD_KEYS.NEED_RADIOLOGY,
			needsRadiology,
			false,
		);
		if (!needsRadiology) {
			formik.setFieldValue(EPIC_FIELD_KEYS.RADIOLOGY_VENDOR, '', false);
			formik.setFieldValue(EPIC_FIELD_KEYS.RADIOLOGY_OTHER, '', false);
		}

		formik.setFieldValue(
			EPIC_FIELD_KEYS.NEED_ANESTHESIOLOGY,
			needsAnesthesiology,
			false,
		);
		if (!needsAnesthesiology) {
			formik.setFieldValue(
				EPIC_FIELD_KEYS.ANESTHESIOLOGY_VENDOR,
				'',
				false,
			);
			formik.setFieldValue(
				EPIC_FIELD_KEYS.ANESTHESIOLOGY_OTHER,
				'',
				false,
			);
		}
		formik.setFieldValue(
			EPIC_FIELD_KEYS.NEED_SPECIALTIES,
			needsSpecialties,
			false,
		);
		if (!needsSpecialties) {
			formik.setFieldValue(EPIC_FIELD_KEYS.SPECIALTIES, null, false);
		} else if (!Array.isArray(formik.values[EPIC_FIELD_KEYS.SPECIALTIES])) {
			// Restore the default row (with isPrimary preselected) after it was previously cleared
			formik.setFieldValue(
				EPIC_FIELD_KEYS.SPECIALTIES,
				SPECIALTIES_INITIAL_VALUES[
					SPECIALTIES_FORM_FIELD_KEYS.SPECIALTIES
				],
				false,
			);
		}

		formik.setFieldValue(
			EPIC_FIELD_KEYS.NEED_RESEARCH,
			needsResearch,
			false,
		);
		if (!needsResearch) {
			formik.setFieldValue(
				EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING,
				'',
				false,
			);
			formik.setFieldValue(
				EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING_DATE,
				'',
				false,
			);
			formik.setFieldValue(
				EPIC_FIELD_KEYS.RESEARCH_STUDY_TYPE,
				'',
				false,
			);
			formik.setFieldValue(EPIC_FIELD_KEYS.RESEARCH_STUDY_ID, '', false);
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Box>
				<CssBaseline />
				<Main open={sOpen}>
					<Formik
						initialValues={{
							...extractValues(
								'defaultValue',
								previousSessionValue,
							),
						}}
						validationSchema={validationSchema}
						validateOnMount
					>
						{formik => (
							<HybridContainer
								formik={formik}
								hasSubmissionBeenAttempted={
									hasSubmissionBeenAttempted
								}
								fieldKeys={EPIC_FIELD_KEYS}
								{...props}
							>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										flexDirection: 'row',
									}}
								>
									<AlertMonitor formik={formik} />
									<FormSection
										id={'primary-section'}
										name={EPIC_FIELD_KEYS.SECTION_MAIN}
										fullWidth
										defaultOpen
									>
										<DefaultDepartmentField
											label={'Default Login Department'}
											name={
												EPIC_FIELD_KEYS.DEFAULT_LOGIN_DEPARTMENT
											}
											formik={formik}
											isRequired
										/>
										<JobCategoryTable
											name={
												EPIC_FIELD_KEYS.JOB_CATEGORIES
											}
											isRequired
											systemID={EPIC_APP_ID}
											setSelection={
												setJobCategoryHelpSelection
											}
											setOpen={setOpen}
											onJobCategoriesChange={jobCategories =>
												onJobCategoriesChange(
													formik,
													jobCategories,
												)
											}
										/>
										{formik.values[
											EPIC_FIELD_KEYS.NEED_DEPARTMENT
										] && (
											<DepartmentSupplement
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_NPI
										] && (
											<NPINumberField
												id={EPIC_FIELD_KEYS.NPI}
												idVerification={
													EPIC_FIELD_KEYS.NPI_VERIFICATION
												}
												trackingNumber={trackingNumber}
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_BILLING
										] && (
											<BillingSupplement
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_RADIOLOGY
										] && (
											<RadiologySupplement
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_ANESTHESIOLOGY
										] && (
											<AnesthesiologySupplement
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_SPECIALTIES
										] && (
											<SpecialtySupplement
												formik={formik}
											/>
										)}
										{formik.values[
											EPIC_FIELD_KEYS.NEED_RESEARCH
										] && (
											<ResearchSupplement
												formik={formik}
											/>
										)}
										{isModificationRequest && (
											<AppWrappedTextbox
												name={EPIC_FIELD_KEYS.COMMENTS}
												label='Comments / Special Instructions'
												formik={formik}
												multiline
												maxRows={3}
												maxLength={100}
											/>
										)}
									</FormSection>
								</Box>
							</HybridContainer>
						)}
					</Formik>
				</Main>
				<HelpBar open={sOpen} setOpen={setOpen}>
					<JobCategoryDirectory
						key={jobCategoryDirectoryKey}
						selection={sJobCategoryHelpSelection}
					/>
				</HelpBar>
			</Box>
		</ThemeProvider>
	);
};

export default Bridges;
